import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article
    ? getRelatedArticles(article.slug, article.categorySlug, 4)
    : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 福利视频网站免费高清在线观看`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">资源不存在</h2>
            <p className="text-sm text-foreground-500 mb-4">该视频资源可能已下架或链接有误</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 mb-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500/90 text-background-50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <span className="text-lg font-bold text-background-50">{article.rating}</span>
                  </div>
                  <span className="text-background-50/80 text-sm">{article.duration}</span>
                </div>
              </div>
            </div>

            <header className="mb-6">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mb-4">
                {article.description}
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  导演：{article.director}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-group-line"></i>
                  </div>
                  主演：{article.cast}
                </div>
                <span>{article.year} | {article.region}</span>
                <span>{(article.viewCount / 10000).toFixed(1)} 万次观看</span>
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700
                prose-h2:text-lg prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-600
                prose-strong:text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                返回{article.category}列表
              </Link>
              <span className="text-xs text-foreground-400">更新于 {article.updateTime}</span>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 pb-12 md:pb-16">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                相关推荐
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-background-950/70 text-background-50 text-xs">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                        {rel.rating}
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="text-xs md:text-sm font-semibold text-foreground-900 line-clamp-1">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-400">{rel.category}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: article.title,
            description: article.description,
            image: article.image,
            url: `https://fulishipin.com/article/${article.slug}`,
            datePublished: article.updateTime,
            director: {
              '@type': 'Person',
              name: article.director,
            },
            actor: article.cast.split('、').map((name) => ({
              '@type': 'Person',
              name: name.trim(),
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: article.rating,
              bestRating: '10',
              ratingCount: article.viewCount,
            },
            countryOfOrigin: {
              '@type': 'Country',
              name: article.region,
            },
          }),
        }}
      />
    </>
  );
}